import { t } from "@lingui/macro";
import {
  useLinkBuilder,
  useNavigation,
  useRoute,
} from "@react-navigation/native";
import graphql from "babel-plugin-relay/macro";
import * as Clipboard from "expo-clipboard";
import { useAtomValue } from "jotai";
import { Platform, Share } from "react-native";
import { useFragment } from "react-relay";

import { currentProfileAtom } from "@/features/Club/atoms";
import { ScreenHeader } from "@whoosh/design-system/src/apps/PatronApp/layout/ScreenHeader";
import { Button } from "@whoosh/design-system/src/components/controls";
import {
  ArrowLeftIcon,
  PersonOutlineIcon,
  ShareIcon,
} from "@whoosh/design-system/src/components/icons_v2/general";
import { useToast } from "@whoosh/design-system/src/components/overlay/Toast/useToast";
import { Box } from "@whoosh/design-system/src/components/primitive";
import { useBreakpointValue } from "@whoosh/design-system/src/hooks/useBreakpointValue";

import { getExpoEASConstant } from "../../../utils/expoConstants";
import { useFlags } from "../../../utils/launchDarkly";
import { useEventDetailLoginRedirect } from "../hooks/useEventDetailLoginRedirect";
import { EventDetailHeader$key } from "./__generated__/EventDetailHeader.graphql";

type EventDetailHeaderProps = {
  fragmentRef: EventDetailHeader$key;
  isScrolled: boolean;
};

export const EventDetailHeader = ({
  fragmentRef,
  isScrolled,
}: EventDetailHeaderProps) => {
  const data = useFragment(EventDetailHeaderFragment, fragmentRef);
  const currentProfile = useAtomValue(currentProfileAtom);
  const isLoggedIn = !!currentProfile?.memberId;
  const { showToast } = useToast();

  const navigation = useNavigation();

  const title = data.eventSeries.name;

  const getAppUrl = () => {
    const env = getExpoEASConstant("deployEnv");
    if (env === "prod") {
      return "https://app.whoosh.io";
    } else if (env === "replica") {
      return "https://replica.whoosh.io";
    } else if (env === "staging") {
      return "https://staging.whoosh.io";
    } else if (env === "qa") {
      return "https://qa.whoosh.io";
    } else if (env === "demo") {
      return "https://demo.whoosh.io";
    } else if (env === "dev") {
      return "https://dev.whoosh.io";
    } else if (env === "local") {
      return "http://localhost:8081";
    } else {
      //Review App -- we get url for production for now
      return "https://app.whoosh.io";
    }
  };

  const { buildHref } = useLinkBuilder();
  const route = useRoute();

  const copyEventUrl = async () => {
    const isLocal =
      Platform.OS === "web" && window.location.host.includes("localhost");
    const protocol = isLocal ? "http" : "https";

    const appUrl = getAppUrl();
    // @TODO this will need to be addresseds for e2e testing
    const link =
      Platform.OS === "web"
        ? isLocal
          ? `${protocol}://localhost:8081${buildHref(route.name, route.params)}`
          : `${protocol}://${window.location.host}${buildHref(
              route.name,
              route.params
            )}`
        : `${appUrl}${buildHref(route.name, route.params)}`;
    if (link) {
      if (Platform.OS === "web") {
        await Clipboard.setStringAsync(link);
        showToast({
          description: t`Copied Event URL`,
          semanticStyle: "success",
          toastId: "copy-event-url",
        });
      } else {
        Share.share({ url: link });
      }
    }
  };

  const isSmallScreen = useBreakpointValue({
    xs: true,
    base: true,
    sm: true,
    md: true,
    lg: false,
  });

  const { newTopbar } = useFlags();
  const handleSignIn = useEventDetailLoginRedirect(
    data.eventSeries.facility?.club?.slug ?? "",
    data.id ?? ""
  );

  return (
    <ScreenHeader
      includeHorizontalPadding={isSmallScreen}
      leftItem={
        <Button
          variant="secondary"
          onPress={() => navigation.goBack()}
          leftIcon={<ArrowLeftIcon />}
          accessibilityLabel={t`Back button`}
        />
      }
      title={title}
      scrolled={isScrolled}
      rightItem={
        isLoggedIn ? (
          <Button
            variant="secondary"
            onPress={copyEventUrl}
            leftIcon={<ShareIcon />}
            accessibilityLabel={t`Share button`}
          />
        ) : !newTopbar ? (
          <Button
            accessibilityLabel={t`Sign In button`}
            testID="event-detail-header-sign-in-button"
            variant="secondary"
            onPress={() => handleSignIn(false)}
            leftIcon={<PersonOutlineIcon fill="white" />}
          >
            {t`Sign In`}
          </Button>
        ) : null
      }
      testID="event-detail-header-screen-header"
      titleRightElement={
        <Box
          flexShrink={0}
          style={{ borderRadius: 2 }}
          width={12}
          height={12}
          backgroundColor={`surface-user-selectable-color${data.eventSeries.color}`}
        />
      }
    />
  );
};

export const EventDetailHeaderFragment = graphql`
  fragment EventDetailHeader on Event {
    id
    eventSeries {
      name
      color
      facility {
        name
        club {
          slug
        }
      }
    }
  }
`;
